<!-- src/views/Dashboard/ReportHistoryPanel.vue -->
<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-md overflow-hidden">
    <!-- 헤더 -->
    <div class="bg-slate-800 text-white px-6 py-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-left text-xl font-bold">보고서 생성 내역</h2>
          <p class="text-slate-300 text-sm mt-1">생성된 보고서 목록을 확인하고 다운로드할 수 있습니다.</p>
        </div>
        <div class="flex items-center gap-3">
          <!-- 폴링 상태 표시 -->
          <div v-if="isPolling" class="flex items-center gap-2 text-sm text-slate-300">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            자동 갱신 중
          </div>
          <button
            @click="fetchReportHistory(false)"
            class="flex items-center gap-2 px-3 py-2 bg-slate-600 hover:bg-slate-500 rounded-md text-sm transition-colors"
            :disabled="isLoading"
          >
            <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            새로고침
          </button>
        </div>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="p-6">
      <!-- 로딩 상태 -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-violet-600"></div>
          <p class="mt-4 text-sm text-gray-600">보고서 내역을 불러오는 중...</p>
        </div>
      </div>

      <!-- 데이터 없음 -->
      <div v-else-if="reportHistory.length === 0" class="text-center py-12">
        <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
        <p class="mt-4 text-gray-500">생성된 보고서가 없습니다.</p>
      </div>

      <!-- 테이블 -->
      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th class="px-4 py-3 text-center font-semibold text-gray-700 w-36">채널</th>
              <th class="px-4 py-3 text-center font-semibold text-gray-700">보고서 제목</th>
              <th class="px-4 py-3 text-center font-semibold text-gray-700 w-32">상태</th>
              <th class="px-4 py-3 text-center font-semibold text-gray-700 w-40">생성일시</th>
              <th class="px-4 py-3 text-center font-semibold text-gray-700 w-32">진행률</th>
              <th class="px-4 py-3 text-center font-semibold text-gray-700 w-40">다운로드</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="report in reportHistory"
              :key="report.task_id"
              class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <!-- 채널 -->
              <td class="px-4 py-3 text-center">
                <div class="flex flex-wrap justify-center gap-1">
                  <span
                    v-for="channel in (report.channels || []).slice(0, 3)"
                    :key="channel"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-violet-50 text-violet-600"
                  >
                    {{ channel }}
                  </span>
                  <span v-if="(report.channels || []).length > 3" class="text-xs text-gray-400">
                    +{{ report.channels.length - 3 }}
                  </span>
                  <span v-if="!report.channels || report.channels.length === 0" class="text-xs text-gray-400">-</span>
                </div>
              </td>
              <!-- 보고서 제목 -->
              <td class="px-4 py-3 text-center">
                <div class="font-medium text-gray-800">{{ report.report_title || '제목 없음' }}</div>
                <div class="text-xs text-gray-400 mt-0.5">{{ report.task_id.slice(0, 8) }}...</div>
              </td>
              <td class="px-4 py-3 text-center">
                <span
                  :class="getStatusClass(report.status)"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                >
                  {{ getStatusText(report.status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-center text-gray-600">
                {{ formatDate(report.created_at) }}
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-col items-center gap-1">
                  <div class="flex items-center gap-2">
                    <div class="w-16 bg-gray-200 rounded-full h-2">
                      <div
                        class="h-2 rounded-full transition-all duration-300"
                        :class="report.status === 'completed' ? 'bg-green-500' : report.status === 'failed' ? 'bg-red-500' : 'bg-violet-500'"
                        :style="{ width: `${report.progress || 0}%` }"
                      ></div>
                    </div>
                    <span class="text-xs text-gray-500 w-8">{{ report.progress || 0 }}%</span>
                  </div>
                  <!-- 현재 단계 표시 -->
                  <span v-if="report.current_step && report.status === 'processing'" class="text-xs text-violet-600">
                    {{ report.current_step }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex items-center justify-center gap-2">
                  <!-- 마크다운 보기 -->
                  <button
                    v-if="report.status === 'completed'"
                    @click="viewReport(report)"
                    class="px-2 py-1 text-xs bg-violet-100 text-violet-700 rounded hover:bg-violet-200 transition-colors"
                    title="보고서 보기"
                  >
                    보기
                  </button>
                  <!-- Word 다운로드 -->
                  <button
                    v-if="report.status === 'completed'"
                    @click="downloadWord(report)"
                    :disabled="downloadingTaskId === report.task_id"
                    class="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50"
                    title="Word 다운로드"
                  >
                    <span v-if="downloadingTaskId === report.task_id">...</span>
                    <span v-else>Word</span>
                  </button>
                  <span v-if="report.status === 'processing'" class="text-xs text-gray-400">생성 중...</span>
                  <span v-if="report.status === 'failed'" class="text-xs text-red-500">실패</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 에러 메시지 -->
      <div v-if="errorMessage" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
        <p class="text-sm text-red-600">{{ errorMessage }}</p>
      </div>
    </div>

    <!-- 보고서 상세 보기 모달 -->
    <div
      v-if="showDetailModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="showDetailModal = false"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden mx-4">
        <!-- 헤더 -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-800">{{ selectedReport?.report_title || '보고서' }}</h3>
          <button
            @click="showDetailModal = false"
            class="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- 마크다운 렌더링 영역 -->
        <div class="p-6 max-h-[600px] overflow-y-auto result-content-box bg-gray-50">
          <div v-if="isLoadingDetail" class="flex items-center justify-center py-12">
            <div class="text-center">
              <div class="inline-block animate-spin rounded-full h-10 w-10 border-4 border-gray-300 border-t-violet-600"></div>
              <p class="mt-4 text-sm text-gray-600">보고서 내용을 불러오는 중...</p>
            </div>
          </div>
          <div
            v-else-if="detailContent"
            class="markdown-body leading-relaxed"
            v-html="renderedDetailContent"
          ></div>
          <div v-else class="text-center py-12 text-gray-500">
            보고서 내용이 없습니다.
          </div>
        </div>

        <!-- 푸터 (Task ID + 버튼) -->
        <div class="px-6 py-3 bg-gray-100 border-t border-gray-200 flex items-center justify-between">
          <div class="text-xs text-gray-400">
            Task ID: {{ selectedReport?.task_id }}
          </div>
          <div class="flex gap-2">
            <button
              @click="downloadWordFromModal"
              :disabled="modalDownloading"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="modalDownloading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              {{ modalDownloading ? '다운로드 중...' : 'Word 다운로드' }}
            </button>
            <button
              @click="showDetailModal = false"
              class="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { marked } from 'marked'

const props = defineProps({
  projectId: {
    type: Number,
    default: null
  }
})

const store = useStore()

const isLoading = ref(false)
const errorMessage = ref('')
const reportHistory = ref([])
const totalCount = ref(0)
const downloadingTaskId = ref(null)

// 폴링 관련
const POLLING_INTERVAL = 1500 // 1.5초
let pollingTimer = null
const isPolling = ref(false)

// 상세 보기 모달
const showDetailModal = ref(false)
const selectedReport = ref(null)
const detailContent = ref('')
const isLoadingDetail = ref(false)
const modalDownloading = ref(false)

const renderedDetailContent = computed(() => {
  if (!detailContent.value) return ''
  return marked(detailContent.value)
})

// 진행 중인 태스크가 있는지 확인
const hasActiveTask = computed(() => {
  return reportHistory.value.some(
    task => task.status === 'pending' || task.status === 'processing'
  )
})

onMounted(() => {
  fetchReportHistory()
})

onUnmounted(() => {
  stopPolling()
})

// projectId가 변경되면 다시 조회
watch(() => props.projectId, (newVal) => {
  if (newVal) {
    stopPolling()
    fetchReportHistory()
  }
})

async function fetchReportHistory(silent = false) {
  // silent 모드가 아닐 때만 로딩 표시 (폴링 시에는 silent)
  if (!silent) {
    isLoading.value = true
  }
  errorMessage.value = ''

  try {
    if (!props.projectId) {
      reportHistory.value = []
      totalCount.value = 0
      return
    }

    // 새 API 호출: GET /api/v1/reports/projects/{project_id}/tasks?limit=20
    const response = await store.dispatch('report/getReportTaskList', {
      projectId: props.projectId,
      limit: 20
    })

    reportHistory.value = response.tasks || []
    totalCount.value = response.total || 0

    // 진행 중인 태스크가 있으면 폴링 시작
    if (hasActiveTask.value) {
      startPolling()
    } else {
      stopPolling()
    }
  } catch (error) {
    errorMessage.value = error.message || '보고서 내역을 불러오는데 실패했습니다.'
    reportHistory.value = []
    totalCount.value = 0
  } finally {
    if (!silent) {
      isLoading.value = false
    }
  }
}

// 폴링 시작
function startPolling() {
  if (pollingTimer) return // 이미 폴링 중이면 무시

  isPolling.value = true
  pollingTimer = setInterval(async () => {
    await fetchReportHistory(true) // silent 모드로 조회
  }, POLLING_INTERVAL)
}

// 폴링 중지
function stopPolling() {
  if (pollingTimer) {
    clearInterval(pollingTimer)
    pollingTimer = null
  }
  isPolling.value = false
}

function getStatusText(status) {
  const statusMap = {
    'pending': '대기 중',
    'processing': '처리 중',
    'completed': '완료',
    'failed': '실패'
  }
  return statusMap[status] || status
}

function getStatusClass(status) {
  const classMap = {
    'pending': 'bg-yellow-100 text-yellow-800',
    'processing': 'bg-violet-100 text-violet-800',
    'completed': 'bg-green-100 text-green-800',
    'failed': 'bg-red-100 text-red-800'
  }
  return classMap[status] || 'bg-gray-100 text-gray-800'
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  // 서버에서 UTC 시간으로 오는 경우, Z가 없으면 추가하여 UTC로 인식시킴
  let utcStr = dateStr
  if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
    utcStr = dateStr + 'Z'
  }
  const date = new Date(utcStr)
  return date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

async function viewReport(report) {
  selectedReport.value = report
  showDetailModal.value = true
  detailContent.value = ''
  isLoadingDetail.value = true

  try {
    const response = await store.dispatch('report/downloadReport', report.task_id)
    detailContent.value = response.content || ''
  } catch (error) {
    detailContent.value = ''
    errorMessage.value = '보고서 내용을 불러오는데 실패했습니다.'
  } finally {
    isLoadingDetail.value = false
  }
}

async function downloadWordFromModal() {
  if (!selectedReport.value) return

  modalDownloading.value = true
  try {
    await store.dispatch('report/downloadWordReport', selectedReport.value.task_id)
  } catch (error) {
    errorMessage.value = error.message || 'Word 파일 다운로드에 실패했습니다.'
  } finally {
    modalDownloading.value = false
  }
}

async function downloadWord(report) {
  downloadingTaskId.value = report.task_id
  errorMessage.value = ''

  try {
    await store.dispatch('report/downloadWordReport', report.task_id)
  } catch (error) {
    errorMessage.value = error.message || 'Word 파일 다운로드에 실패했습니다.'
  } finally {
    downloadingTaskId.value = null
  }
}
</script>

<style scoped>
/* GPT-style markdown */
:deep(.markdown-body) {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans KR", sans-serif;
  font-size: 15px;
  line-height: 1.75;
  color: #1f2937;
  word-break: keep-all;
  text-align: left;
  max-width: 1200px;
  margin: 0 auto;
}

/* Headings */
:deep(.markdown-body h1) {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 2rem 0 1rem;
}

:deep(.markdown-body h2) {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.8rem 0 0.8rem;
}

:deep(.markdown-body h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.5rem 0 0.6rem;
}

:deep(.markdown-body h4) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 1.2rem 0 0.5rem;
}

/* Paragraph */
:deep(.markdown-body p) {
  margin: 0.75rem 0;
}

/* Strong / Em */
:deep(.markdown-body strong) {
  font-weight: 600;
}

:deep(.markdown-body em) {
  font-style: italic;
}

/* Lists */
:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  margin: 0.8rem 0 0.8rem 1.25rem;
  padding: 0;
}

:deep(.markdown-body li) {
  margin: 0.4rem 0;
}

/* Inline code */
:deep(.markdown-body code) {
  background: #f3f4f6;
  color: #111827;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* Code block */
:deep(.markdown-body pre) {
  background: #f3f4f6;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1rem 0;
}

:deep(.markdown-body pre code) {
  background: transparent;
  padding: 0;
  font-size: 0.9rem;
}

/* Blockquote */
:deep(.markdown-body blockquote) {
  margin: 1rem 0;
  padding-left: 1rem;
  color: #4b5563;
  border-left: 4px solid #e5e7eb;
}

/* Tables */
:deep(.markdown-body table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;
}

:deep(.markdown-body th),
:deep(.markdown-body td) {
  border: 1px solid #e5e7eb;
  padding: 0.5rem 0.75rem;
  text-align: left;
}

:deep(.markdown-body th) {
  background: #f9fafb;
  font-weight: 600;
}

/* Links */
:deep(.markdown-body a) {
  color: #7c3aed;
  text-decoration: underline;
}

/* Scrollbar */
:deep(.result-content-box::-webkit-scrollbar) { width: 8px; height: 8px; }
:deep(.result-content-box::-webkit-scrollbar-track) { background: #f1f5f9; border-radius: 10px; }
:deep(.result-content-box::-webkit-scrollbar-thumb) { background: #cbd5e1; border-radius: 10px; }
:deep(.result-content-box::-webkit-scrollbar-thumb:hover) { background: #94a3b8; }
:deep(.result-content-box) { scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9; }
</style>
